import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import "./styles.css";
import Latex from "react-latex-next";
import NextAction from "../../utils/NextAction";

export default function Controls({ controls }) {
  const {
    l1,
    l2,
    l3,
    l4,
    setL1,
    setL2,
    setL3,
    setL4,
    alpha,
    setAlpha,
    epsilon,
    setEpsilon,
    showJacobian,
    setShowJacobian,
    showIteration,
    setShowIteration,
    setRun,
    setIteration,
    setStep,
  } = controls;
  return (
    <div className="controls">
      <div>
        <Typography variant="h5" style={{ paddingBottom: 20 }}>
          2D Jacobian Inverse Kinematics
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Here are the parameters of 2D Jacobian Inverse Kinematics. Feel free
          to change the value.
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{ display: "flex", flexDirection: "column", marginRight: 4 }}
          >
            <div className="control">
              <label>
                <Latex>$l_1$</Latex>
              </label>
              <input
                value={l1}
                onChange={(e) => setL1(parseInt(e.target.value))}
                step={1}
                type="number"
              />
            </div>
            <div className="control">
              <label>
                <Latex>$l_2$</Latex>
              </label>
              <input
                value={l2}
                onChange={(e) => setL2(parseInt(e.target.value))}
                step={1}
                type="number"
              />
            </div>
            <div className="control">
              <label>
                <Latex>$l_3$</Latex>
              </label>
              <input
                value={l3}
                onChange={(e) => setL3(parseInt(e.target.value))}
                step={1}
                type="number"
              />
            </div>
            <div className="control">
              <label>
                <Latex>$l_4$</Latex>
              </label>
              <input
                value={l4}
                onChange={(e) => setL4(parseInt(e.target.value))}
                step={1}
                type="number"
              />
            </div>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <div className="control">
              <label>
                <Latex>$\alpha$</Latex>
              </label>
              <input
                value={alpha}
                onChange={(e) => setAlpha(parseFloat(e.target.value))}
                step={0.1}
                type="number"
              />
            </div>
            <div className="control">
              <label>
                <Latex>$\epsilon$</Latex>
              </label>
              <input
                value={epsilon}
                onChange={(e) => {
                  if (parseFloat(e.target.value) > 0)
                    setEpsilon(parseFloat(e.target.value));
                }}
                step={0.1}
                type="number"
              />
            </div>
          </Box>
        </Box>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={showIteration}
                onClick={() => {
                  setShowIteration(!showIteration);
                  setShowJacobian(false);
                  setRun(true);
                  setIteration(0);
                  setStep(0);
                }}
              />
            }
            label="Slow Animation and Show the result of each Iteration"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showJacobian}
                onClick={() => {
                  setShowJacobian(!showJacobian);
                  setShowIteration(false);
                  setRun(true);
                  setIteration(0);
                  setStep(0);
                }}
              />
            }
            label="Show Jacobian"
          />
        </FormGroup>
        {showJacobian && (
          <Typography style={{ paddingBottom: 20, textAlign: "left" }}>
            Assume all angles rotate clockwise:
            <br />
            <b style={{ color: "green" }}>Green</b>: <Latex>$j_1$</Latex>,
            Jacobian of <Latex>$\theta_1$</Latex>
            <br />
            <b style={{ color: "blue" }}>Blue</b>: <Latex>$j_2$</Latex>,
            Jacobian of <Latex>$\theta_2$</Latex>
            <br />
            <b style={{ color: "orange" }}>Orange</b>: <Latex>$j_3$</Latex>,
            Jacobian of <Latex>$\theta_3$</Latex>
            <br />
            <b style={{ color: "red" }}>Red</b>: <Latex>$j_4$</Latex>, Jacobian
            of <Latex>$\theta_4$</Latex>
            <br />
            <b style={{ color: "fuchsia" }}>Fuchsia</b>: Total Jacobian of above
          </Typography>
        )}
        <Typography
          variant="h6"
          style={{ paddingTop: 20, textAlign: "left", fontWeight: "bold" }}
        >
          1. Compute Forward Kinematics
        </Typography>
        <Typography style={{ paddingBottom: 20, textAlign: "left" }}>
          To understand the 2D Jacobian IK, we have to calculate the forward
          kinematics of this 4 DoF planar robot with four revolute joints.
          <br />
          <br />
          The parameters are:
          <br />- Joint1: <Latex>$\theta_1$, length $l_1$</Latex>
          <br />- Joint2: <Latex>$\theta_2$, length $l_2$</Latex>
          <br />- Joint3: <Latex>$\theta_3$, length $l_3$</Latex>
          <br />- Joint4: <Latex>$\theta_4$, length $l_4$</Latex>
          <br />
          <br />
          The position of end-effector <Latex>$(x,y)$</Latex> is:
          <br />{" "}
          <Latex>
            $x = l_1cos(\theta_1) + l_2cos(\theta_1+\theta_2) +
            l_3cos(\theta_1+\theta_2+\theta_3) +
            l_4cos(\theta_1+\theta_2+\theta_3+\theta_4)$
          </Latex>
          <br />{" "}
          <Latex>
            $y = l_1sin(\theta_1) + l_2sin(\theta_1+\theta_2) +
            l_3sin(\theta_1+\theta_2+\theta_3) +
            l_4sin(\theta_1+\theta_2+\theta_3+\theta_4)$
          </Latex>
          <br />{" "}
          <Latex>
            {` $$\\bold{p} = \\left[ \\begin{array}
          {c} x  \\\\ y \\\\ \\end{array}
          \\right] $$`}
          </Latex>
        </Typography>
        <Typography
          variant="h6"
          style={{ textAlign: "left", fontWeight: "bold" }}
        >
          2. Formulate the Jacobian Matrix
        </Typography>
        <Typography style={{ paddingBottom: 20, textAlign: "left" }}>
          We calculate the Jacobian Matrix of this arm now:
          <br />
          <br />
          <Latex>
            {` $$J = \\left[ \\begin{array}
          {cccc} \\frac{\\partial x}{\\partial \\theta_1}  & \\frac{\\partial x}{\\partial \\theta_2} & \\frac{\\partial x}{\\partial \\theta_3} & \\frac{\\partial x}{\\partial \\theta_4} \\\\ \\frac{\\partial y}{\\partial \\theta_1} & \\frac{\\partial y}{\\partial \\theta_2} & \\frac{\\partial y}{\\partial \\theta_3} & \\frac{\\partial y}{\\partial \\theta_4} \\\\ \\end{array}
          \\right]$$`}
            {` $$ = 
          \\left[ \\begin{array}
            {cccc} \\frac{\\partial \\bold{p}}{\\partial \\theta_1}  & \\frac{\\partial \\bold{p}}{\\partial \\theta_2} & \\frac{\\partial \\bold{p}}{\\partial \\theta_3} & \\frac{\\partial \\bold{p}}{\\partial \\theta_4}  \\\\ \\end{array}
          \\right] 
          $$`}
            {`$$ =  
          \\left[ \\begin{array}
            {cccc} j_1  & j_2 & j_3 & j_4  \\\\ \\end{array}
          \\right] 
          $$`}
          </Latex>
          <Latex>{`$$\\dot{\\bold{p}} = J\\dot{\\theta}$$`}</Latex>
        </Typography>
        <Typography
          variant="h6"
          style={{ paddingTop: 20, textAlign: "left", fontWeight: "bold" }}
        >
          3. Inverse Kinematics for Velocity
        </Typography>
        <Typography style={{ paddingBottom: 20, textAlign: "left" }}>
          Given a desired end-effector velocity{" "}
          <Latex>{`$\\dot{\\bold{p}}$`}</Latex> in Cartesian space, solve for{" "}
          <Latex>{`$\\dot{\\theta}$`}</Latex>:
          <br />
          <br />
          <Latex>{`$$\\dot{\\theta} = J^{-1}\\dot{\\bold{p}}$$`}</Latex>
          If the Jacobian is not square, compute the pseudo-inverse{" "}
          <Latex>{`$J^{+}$`}.</Latex>
        </Typography>
        <Typography
          variant="h6"
          style={{ paddingTop: 20, textAlign: "left", fontWeight: "bold" }}
        >
          4. Iterative Solution for Position Control
        </Typography>
        <Typography style={{ paddingBottom: 20, textAlign: "left" }}>
          1. Suppose we want the end-effector to move to position{" "}
          <Latex>{`$(x_d,y_d)$`}</Latex>
          <br />
          <br />
          2. Initialize <Latex>{`$\\theta_1, \\theta_2$`}</Latex> with some
          values.
          <br />
          <br />
          3. Compute the current end-effector position{" "}
          <Latex>{`$(x,y)$`}</Latex> using forward kinematics.
          <br />
          <br />
          4. Because we cannot get the velocity from point3, we use position
          error instead.
          <br />
          <Latex>{`$\\Delta x = x_d - x, \\Delta y = y_d - y$`}</Latex>
          <br />
          <br />
          5. Use the Jacobian to update the joint variables
          <br />
          <Latex>{`$\\Delta \\theta = \\alpha J^{+} \\left[\\begin{array} {c} \\Delta x \\\\ \\Delta y \\end{array}\\right]$`}</Latex>
          <br />
          <Latex>$\alpha$</Latex> is the step ratio which determines the step
          size of each move.
          <br />
          <br />
          6. Update the joint angles:{" "}
          <Latex>{`$\\theta \\leftarrow \\theta + \\Delta \\theta$`}</Latex>
          <br />
          <br />
          7. Repeat until the position error is sufficiently small (under
          threshold{" "}
          <Latex>{`$\\sqrt{\\Delta x^2 + \\Delta y^2} \\leq \\epsilon$`}</Latex>
          ).
        </Typography>
        <NextAction label={"Next: Lagrange Dynamics Equation"} to="/lagrange" />
      </div>
    </div>
  );
}
